import { useMemo } from 'react'
import { useApp } from '@/context/AppContext'
import { useFilteredBreakValues } from '@/hooks/useFilteredBreakValues'
import { HorizontalStackedBar } from '@/components/charts/HorizontalStackedBar'
import { buildFilterKey } from '@/services/filterEngine'
import { getResponseColor } from '@/utils/colors'
import type { ScaleType } from '@/types'

// Response order: Strongly Agree → Agree → Unsure → Disagree → Strongly Disagree (case-insensitive)
// Note: Data has inconsistent neutral labels: "Neutral / Unsure", "Neutral /Unsure", "Neutral Unsure"
const AGREE_RESPONSE_ORDER = ['strongly agree', 'somewhat agree', 'agree', 'neutral / unsure', 'neutral /unsure', 'neutral unsure', 'unsure', 'neutral', 'disagree', 'somewhat disagree', 'strongly disagree']

// Sort responses according to agree/disagree order (Unsure in the middle) - case-insensitive matching
const sortAgreeResponses = (responses: Array<{ label: string; percentage: number }>) => {
  return [...responses].sort((a, b) => {
    const aIdx = AGREE_RESPONSE_ORDER.indexOf(a.label.toLowerCase())
    const bIdx = AGREE_RESPONSE_ORDER.indexOf(b.label.toLowerCase())
    if (aIdx === -1) return 1
    if (bIdx === -1) return -1
    return aIdx - bIdx
  })
}

// EnvHealth response order: Much improved → The same → Much worse
const ENVHEALTH_ORDER = ['Much improved', 'Somewhat improved', 'The same', 'Somewhat worse', 'Much worse']

// GovEffort response order: Far too much → About right → Far too little
const GOVEFFORT_ORDER = ['Far too much', 'Too much', 'About right', 'Too little', 'Far too little']

// Sort helper - also filters out "No opinion" responses
const sortByOrder = (responses: Array<{ label: string; percentage: number }>, order: string[]) => {
  return [...responses]
    .filter(r => r.label !== 'No opinion') // Remove "No opinion" from display
    .sort((a, b) => {
      const aIdx = order.indexOf(a.label)
      const bIdx = order.indexOf(b.label)
      if (aIdx === -1) return 1
      if (bIdx === -1) return -1
      return aIdx - bIdx
    })
}

// Legend labels
const LEGEND_LABELS = ['Strongly Agree', 'Somewhat Agree', 'Neutral / Unsure', 'Somewhat Disagree', 'Strongly Disagree']

// Exact question order from Tableau Protected Areas dashboard (by zone y-coordinate)
const PROTECTED_AREAS_QUESTIONS = [
  { id: 'Agree_TimeInNature', label: 'Spending time in nature is very important to me.' },
  { id: 'Agree_MoreProtectedAreas', label: 'Your province needs more protected areas that are permanently off-limits to industrial development.' },
  { id: 'Agree_NoHuntFishAreas', label: 'Certain places in nature should be off-limits to ALL hunting and fishing.' },
  { id: 'Agree_ProtectionGoodForBiz', label: 'Permanently protecting land for conservation is good for business because it creates certainty about what is and is not open for development.' },
  { id: 'Agree_HumanMeddling', label: 'Humans meddle too much with nature and the natural order.' },
  { id: 'Agree_WealthyRestrictNature', label: "These days, it's wealthy landowners, developers, and private resource companies that shut people out of the backcountry and wilderness -- not First Nations." },
  { id: 'Agree_ReducationalHuntFish', label: 'Recreational hunting and fishing are key to maintaining the balance of nature.' },
  { id: 'Agree_IndustryWaterUse', label: 'Industry should have a greater say in decisions affecting water use and watersheds in my region.' },
  { id: 'Agree_IndustryLandUse', label: 'Industry should have a greater say in decisions affecting land use in my region.' },
] as const

export function ProtectedAreas() {
  const { state } = useApp()
  const { data, filters } = state
  const { selectedBreak, selectedProvince } = filters

  const breakDef = data?.breaks.find(b => b.id === selectedBreak)
  const { breakValues: filteredBreakValues } = useFilteredBreakValues()
  const breakValues = selectedBreak !== 'total' && breakDef ? filteredBreakValues : ['total']
  const isBreakActive = selectedBreak !== 'total'
  const breakName = breakDef?.name || 'Total'
  const displayMap = breakDef?.displayMap || {}
  const getDisplayName = (value: string) => displayMap[value] || value

  // Sort questions by agreement level (Strongly Agree + Somewhat Agree) - highest first
  const sortedQuestions = useMemo(() => {
    if (!data) return PROTECTED_AREAS_QUESTIONS

    const totalFilterKey = buildFilterKey('total', 'total')

    return [...PROTECTED_AREAS_QUESTIONS].sort((a, b) => {
      const aData = data.questions[a.id]?.data[totalFilterKey]
      const bData = data.questions[b.id]?.data[totalFilterKey]

      const getAgreeScore = (qData: typeof aData) => {
        if (!qData) return 0
        const stronglyAgree = qData.responses.find(r => r.label.toLowerCase() === 'strongly agree')?.percentage || 0
        const somewhatAgree = qData.responses.find(r => r.label.toLowerCase() === 'somewhat agree' || r.label.toLowerCase() === 'agree')?.percentage || 0
        return stronglyAgree + somewhatAgree
      }

      return getAgreeScore(bData) - getAgreeScore(aData)
    })
  }, [data])

  // Helper to render a single question bar
  const renderQuestionBars = (
    questionId: string,
    scaleType: ScaleType,
    sortFn: (responses: Array<{ label: string; percentage: number }>) => Array<{ label: string; percentage: number }>
  ) => {
    const question = data?.questions[questionId]
    if (!question) return null

    const rows = breakValues.map(value => {
      const filterKey = buildFilterKey('total', selectedBreak, isBreakActive ? value : undefined, selectedProvince)
      let questionData = question.data[filterKey]
      if (!questionData && selectedProvince) {
        questionData = question.data[buildFilterKey('total', selectedBreak, isBreakActive ? value : undefined)]
      }
      return { value, filterKey, data: questionData }
    }).filter(row => row.data)

    if (rows.length === 0) return null

    return (
      <div className="space-y-2">
        {rows.map(row => (
          <div key={row.filterKey} className="flex items-center gap-2">
            <div className="w-32 text-xs text-slate-500 dark:text-slate-400 text-right shrink-0" title={isBreakActive ? getDisplayName(row.value) : 'National Sample'}>
              {isBreakActive ? getDisplayName(row.value) : 'National Sample'}
            </div>
            <div className="flex-1">
              <HorizontalStackedBar
                segments={sortFn(row.data!.responses)}
                scaleType={scaleType}
                height={isBreakActive ? 24 : 28}
                showLabels={false}
                normalize={true}
              />
            </div>
          </div>
        ))}
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-800 dark:text-white">Protected Areas</h1>
        <span className="text-sm text-slate-500 dark:text-slate-400">Break: {breakName}</span>
      </div>

      {/* Health of Nature Comparison */}
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm p-6">
        <h2 className="text-base font-semibold text-slate-700 dark:text-slate-300 mb-4">
          Compared to 10 years ago, would you say the health of nature and the natural environment in your region is...
        </h2>
        {/* Legend */}
        <div className="flex flex-wrap gap-4 mb-4 pb-4 border-b border-slate-100 dark:border-slate-700">
          {ENVHEALTH_ORDER.map(label => (
            <div key={label} className="flex items-center gap-2">
              <div
                className="w-4 h-4 rounded"
                style={{ backgroundColor: getResponseColor('envhealth', label) }}
              />
              <span className="text-xs text-slate-600 dark:text-slate-400">{label}</span>
            </div>
          ))}
        </div>
        {renderQuestionBars('EnvHealth_10YrComp', 'envhealth', (r) => sortByOrder(r, ENVHEALTH_ORDER))}
      </div>

      {/* Provincial Government Effort */}
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm p-6">
        <h2 className="text-base font-semibold text-slate-700 dark:text-slate-300 mb-4">
          Thinking of efforts to protect lands, forests, fresh water, wildlife and nature in your province, would you say your provincial government is doing...
        </h2>
        {/* Legend */}
        <div className="flex flex-wrap gap-4 mb-4 pb-4 border-b border-slate-100 dark:border-slate-700">
          {GOVEFFORT_ORDER.map(label => (
            <div key={label} className="flex items-center gap-2">
              <div
                className="w-4 h-4 rounded"
                style={{ backgroundColor: getResponseColor('goveffort', label) }}
              />
              <span className="text-xs text-slate-600 dark:text-slate-400">{label}</span>
            </div>
          ))}
        </div>
        {renderQuestionBars('ProvGovt_EnvEffort', 'goveffort', (r) => sortByOrder(r, GOVEFFORT_ORDER))}
      </div>

      {/* Agree/Disagree Statements */}
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm p-6">
        <h2 className="text-base font-semibold text-slate-700 dark:text-slate-300 mb-6">
          Please say whether you AGREE or DISAGREE with each of the following statements.
        </h2>

        {/* Legend */}
        <div className="flex flex-wrap gap-4 mb-6 pb-4 border-b border-slate-100 dark:border-slate-700">
          {LEGEND_LABELS.map(label => (
            <div key={label} className="flex items-center gap-2">
              <div
                className="w-4 h-4 rounded"
                style={{ backgroundColor: getResponseColor('likert5', label) }}
              />
              <span className="text-xs text-slate-600 dark:text-slate-400">{label}</span>
            </div>
          ))}
        </div>

        <div className="space-y-6">
          {!data ? (
            <p className="text-slate-500 dark:text-slate-400">Loading...</p>
          ) : (
            sortedQuestions.map(({ id, label }) => {
              const question = data.questions[id]
              if (!question) return null

              const rows = breakValues.map(value => {
                const filterKey = buildFilterKey('total', selectedBreak, isBreakActive ? value : undefined, selectedProvince)
                let questionData = question.data[filterKey]
                if (!questionData && selectedProvince) {
                  questionData = question.data[buildFilterKey('total', selectedBreak, isBreakActive ? value : undefined)]
                }
                return { value, filterKey, data: questionData }
              }).filter(row => row.data)

              if (rows.length === 0) return null

              return (
                <div key={id} className="border-b border-slate-100 dark:border-slate-700 pb-4 last:border-0 last:pb-0">
                  <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">{label}</h3>
                  <div className="space-y-2">
                    {rows.map(row => (
                      <div key={row.filterKey} className="flex items-center gap-2">
                        <div className="w-32 text-xs text-slate-500 dark:text-slate-400 text-right shrink-0" title={isBreakActive ? getDisplayName(row.value) : 'National Sample'}>
                          {isBreakActive ? getDisplayName(row.value) : 'National Sample'}
                        </div>
                        <div className="flex-1">
                          <HorizontalStackedBar
                            segments={sortAgreeResponses(row.data!.responses)}
                            scaleType="likert5"
                            height={isBreakActive ? 24 : 28}
                            showLabels={false}
                            normalize={true}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )
            })
          )}
        </div>
      </div>
    </div>
  )
}
